import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  Grid,
  LinearProgress,
  Stack,
  Typography,
  linearProgressClasses,
  styled,
} from '@mui/material';
import React from 'react';
import { UserNutrientSummary } from '../../types';

const modalBox = {
  position: 'absolute' as 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  minWidth: { xs: '85%', md: '40%' },
  maxWidth: { xs: '85%', md: '50%' },
  minHeight: { xs: '40%', md: '75%' },
  maxHeight: { xs: '90%', md: '75%' },
  bgcolor: 'background.paper',
  borderRadius: 3,
  overflow: 'scroll',
  '&::-webkit-scrollbar': { display: 'none' },
};

export interface NutrientCardProps {
  nutrient?: UserNutrientSummary;
  onClose?: () => void;
}

export const NutrientCard = (props: NutrientCardProps): JSX.Element => {
  const defaultMax = 100;
  const BorderLinearProgress = styled(LinearProgress)(({ theme }) => ({
    height: 10,
    [`&.${linearProgressClasses.colorPrimary}`]: {
      backgroundColor: theme.palette.primary.main,
    },
    [`& .${linearProgressClasses.bar}`]: {
      background:
        'linear-gradient(90deg, #0075FF 0%, #0678FC 43.33%, #99D0B1 62.25%, #E9FF88 111.71%), #0075FF',
    },
  }));

  const renderProgressBar = (value: string | undefined): JSX.Element => {
    return (
      <Grid item xs={12} md={6}>
        <BorderLinearProgress
          variant="determinate"
          value={(parseInt(value || '0') / defaultMax) * 100}
          sx={{
            position: { md: 'relative' },
            margin: { md: 'auto' },
            top: { md: 35 },
          }}
        />
        <Grid
          container
          item
          justifyContent="space-between"
          sx={{
            position: { md: 'relative' },
            margin: { md: 'auto' },
            top: { md: 45 },
          }}
        >
          <Typography
            variant="light"
            color="text.secondary"
            sx={{ fontSize: 12 }}
          >
            Low
          </Typography>
          <Typography
            variant="light"
            color="text.secondary"
            sx={{ fontSize: 12 }}
          >
            Normal
          </Typography>
          <Typography
            variant="light"
            color="text.secondary"
            sx={{ fontSize: 12 }}
          >
            High
          </Typography>
        </Grid>
      </Grid>
    );
  };

  return (
    <>
      <Box sx={modalBox}>
        <Grid container sx={{ padding: 5 }} spacing={5} justifyContent="center">
          <Grid
            item
            container
            xs={12}
            direction="column"
            alignItems="flex-end"
            justifyContent="flex-start"
          >
            <CloseIcon
              color="disabled"
              onClick={props?.onClose}
              sx={{
                cursor: 'pointer',
              }}
            />
          </Grid>
          <Grid
            item
            xs={12}
            container
            justifyContent="center"
            sx={{ paddingLeft: 10 }}
          >
            <Stack>
              <Typography
                variant="bold"
                sx={{
                  fontSize: { xs: 18, md: 24 },
                  textAlign: 'center',
                }}
              >
                {props?.nutrient?.title}
              </Typography>
              <Typography
                variant="light"
                color="text.secondary"
                sx={{
                  fontSize: { xs: 12, md: 16 },
                  textAlign: 'center',
                }}
              >
                Your daily dosage
              </Typography>
              {props?.nutrient?.mg && props?.nutrient?.unit && (
                <Typography
                  variant="bold"
                  component="span"
                  sx={{
                    color: 'info.main',
                    fontSize: { xs: 72 },
                    textAlign: 'center',
                  }}
                >
                  {Math.round(parseFloat(props.nutrient.mg) * 10) / 10}

                  <Typography
                    variant="inherit"
                    component="span"
                    sx={{
                      color: 'info.main',
                      fontSize: { xs: 24 },
                      textAlign: 'center',
                      paddingLeft: 1,
                    }}
                  >
                    {props?.nutrient?.unit}
                  </Typography>
                </Typography>
              )}
            </Stack>
          </Grid>
          <Grid item md={12}>
            <Typography
              variant="mono"
              sx={{
                fontSize: 12,
                textDecoration: 'underline',
                textDecorationStyle: 'dashed',
                textUnderlineOffset: 5,
              }}
            >
              YOUR DOSAGE CALCULATION:
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography
              variant="light"
              color="text.secondary"
              padding="5px 5px 5px 0px"
              component="p"
            >
              Questionnaire
            </Typography>
            <Typography variant="bold" component="p" align="left">
              {props?.nutrient?.questionnaire_result_text}
            </Typography>
          </Grid>
          {renderProgressBar(props?.nutrient?.questionnaire_result)}
          <Grid item xs={12} md={6}>
            <Typography
              variant="light"
              color="text.secondary"
              padding="5px 5px 5px 0px"
              component="p"
            >
              Blood
            </Typography>
            <Typography variant="bold" component="p" align="left">
              {props?.nutrient?.blood_result_text}
            </Typography>
          </Grid>
          {renderProgressBar(props?.nutrient?.blood_result)}
          <Grid item xs={12} md={6}>
            <Typography
              variant="light"
              color="text.secondary"
              padding="5px 5px 5px 0px"
              component="p"
            >
              DNA
            </Typography>
            <Typography variant="bold" component="p" align="left">
              {props?.nutrient?.dna_result_text}
            </Typography>
          </Grid>
          {renderProgressBar(props?.nutrient?.dna_result)}
          {props?.nutrient?.cortisol_result !== undefined && (
            <Grid item xs={12} md={6}>
              <Typography
                variant="light"
                color="text.secondary"
                padding="5px 5px 5px 0px"
                component="p"
              >
                Cortisol
              </Typography>
              <Typography variant="bold" component="p" align="left">
                {props?.nutrient?.cortisol_result_text}
              </Typography>
            </Grid>
          )}
          {props?.nutrient?.cortisol_result !== undefined &&
            renderProgressBar(props?.nutrient?.cortisol_result)}
          <Grid item xs={12} md={6}>
            <Typography
              variant="light"
              color="text.secondary"
              padding="5px 5px 5px 0px"
              component="p"
            >
              Did you know?
            </Typography>
            <Typography sx={{ fontSize: 14 }}>
              {props?.nutrient?.text}
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography
              variant="light"
              color="text.secondary"
              padding="5px 5px 5px 0px"
              component="p"
              sx={{ fontSize: 16 }}
            >
              {`${props?.nutrient?.title} is involved in`}
            </Typography>
            {props?.nutrient && (
              <ul>
                {JSON.parse(props?.nutrient?.about_the_vitamin || '[]').map(
                  (fact: string, i: number) => {
                    return (
                      <li key={`fact${i}`}>
                        <Typography sx={{ fontSize: 12 }}>{fact}</Typography>
                      </li>
                    );
                  }
                )}
              </ul>
            )}
          </Grid>
        </Grid>
      </Box>
    </>
  );
};
